import React, { Suspense } from 'react';
import type { ProductDesignerConfig } from '@customedge/shared';
import { useTranslation } from 'react-i18next';
import { useDesignerInit } from './hooks/useDesignerInit';
import { useProductSetup } from './hooks/useProductSetup';
import { DesignerApp } from './components/layout/DesignerApp';
import { NotificationsProvider } from './components/Notifications';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ApprovalPage } from './components/ApprovalPage';
import { Model3DPreview } from './components/canvas/Model3DPreview';
import { DesignWidgetRoot } from './components/DesignWidgetRoot';
import { DesignDownloadRoot } from './components/DesignDownloadRoot';
import { DesignCaptureRoot } from './components/DesignCaptureRoot';

function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTopColor: 'var(--selection)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#64748b', fontSize: 14 }}>{t('designer.loading')}</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 12 }}>
      <p style={{ color: '#ef4444', fontSize: 16, fontWeight: 600 }}>{t('designer.loadingError')}</p>
      <p style={{ color: '#64748b', fontSize: 14 }}>{message}</p>
    </div>
  );
}

// `/approval/:token` — public, no-login design-approval page (see
// ApprovalPage.tsx). Checked before useDesignerInit() runs at all: that hook
// expects a `sku`/`config` param and immediately rewrites the URL via
// replaceState, which would corrupt this route's own path-based token.
function useApprovalToken(): string | null {
  const match = window.location.pathname.match(/^\/approval\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// `?mode=widget&design={id}` — a public, embeddable 2D/3D preview keyed by
// design id, meant to be iframed into Magento's cart/order pages and the
// CustomEdge admin Orders view (see DesignWidgetRoot.tsx). Checked at the same
// tier as useApprovalToken() above, before useDesignerInit() ever runs — that
// hook expects a `sku`/`config` param and would misreport a missing SKU for
// this design-id-keyed route.
function useWidgetParams(): { isWidgetMode: boolean; designId: string | null } {
  const params = new URLSearchParams(window.location.search);
  return {
    isWidgetMode: params.get('mode') === 'widget',
    designId:     params.get('design'),
  };
}

// `?mode=download&design={id}&format={pdf|png|jpg|psd}&downloadToken={jwt}` —
// see DesignDownloadRoot.tsx's own comment for the full rationale, and
// generateDownloadToken (backend/middleware/auth.ts) for `downloadToken`
// specifically. Same tier as the widget/approval checks above, before
// useDesignerInit() ever runs.
function useDownloadParams(): { isDownloadMode: boolean; designId: string | null; format: string | null; downloadToken: string | null } {
  const params = new URLSearchParams(window.location.search);
  return {
    isDownloadMode: params.get('mode') === 'download',
    designId:       params.get('design'),
    format:         params.get('format'),
    downloadToken:  params.get('downloadToken'),
  };
}

// `?mode=capture&design={id}` — see DesignCaptureRoot.tsx's own comment.
// Same tier as the modes above, before useDesignerInit() ever runs.
function useCaptureParams(): { isCaptureMode: boolean; designId: string | null } {
  const params = new URLSearchParams(window.location.search);
  return {
    isCaptureMode: params.get('mode') === 'capture',
    designId:      params.get('design'),
  };
}

export default function App() {
  const approvalToken = useApprovalToken();
  if (approvalToken) {
    return (
      <NotificationsProvider>
        <ApprovalPage token={approvalToken} />
      </NotificationsProvider>
    );
  }

  const { isWidgetMode, designId } = useWidgetParams();
  if (isWidgetMode && designId) {
    return (
      <NotificationsProvider>
        <DesignWidgetRoot designId={designId} />
      </NotificationsProvider>
    );
  }

  const { isDownloadMode, designId: downloadDesignId, format, downloadToken } = useDownloadParams();
  if (isDownloadMode && downloadDesignId && (format === 'pdf' || format === 'png' || format === 'jpg' || format === 'psd')) {
    return (
      <NotificationsProvider>
        <DesignDownloadRoot designId={downloadDesignId} format={format} downloadToken={downloadToken} />
      </NotificationsProvider>
    );
  }

  const { isCaptureMode, designId: captureDesignId } = useCaptureParams();
  if (isCaptureMode && captureDesignId) {
    return <DesignCaptureRoot designId={captureDesignId} />;
  }

  return <DesignerAppRoot />;
}

// `mode=3d-preview` — a bare, chrome-free 3D Preview (+ variant selector),
// meant to be iframed directly into a host PDP tab (see widget/src/Model3DWidget.tsx)
// rather than the full designer. Checked the same way as `isPopupMode` below;
// `useDesignerInit` already preserves `mode` through its URL cleanup.
function is3dPreviewModeFromSearch(search: string): boolean {
  return new URLSearchParams(search).get('mode') === '3d-preview';
}

function DesignerAppRoot() {
  const { config, token, error, isPopupMode, isAdminMode, brandLogoUrl, brandLogoLink } = useDesignerInit();
  const is3dPreviewMode = is3dPreviewModeFromSearch(window.location.search);

  return (
    <NotificationsProvider>
      {error ? (
        <ErrorScreen message={error} />
      ) : !config ? (
        <LoadingScreen />
      ) : is3dPreviewMode ? (
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen />}>
            <Model3DPreviewRoot config={config} />
          </Suspense>
        </ErrorBoundary>
      ) : (
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen />}>
            <DesignerAppWithSetup config={config} token={token} isPopupMode={isPopupMode} isAdminMode={isAdminMode} brandLogoUrl={brandLogoUrl} brandLogoLink={brandLogoLink} />
          </Suspense>
        </ErrorBoundary>
      )}
    </NotificationsProvider>
  );
}

function DesignerAppWithSetup({ config, token, isPopupMode, isAdminMode, brandLogoUrl, brandLogoLink }: { config: ProductDesignerConfig; token: string | null; isPopupMode: boolean; isAdminMode: boolean; brandLogoUrl: string | null; brandLogoLink: string | null }) {
  useProductSetup(config);
  return <DesignerApp isPopupMode={isPopupMode} isAdminMode={isAdminMode} brandLogoUrl={brandLogoUrl} brandLogoLink={brandLogoLink} />;
}

// Standalone host for `Model3DPreview` — same product-config bootstrap
// (`useProductSetup`) as the full designer, but none of `DesignerApp`'s tabs/
// toolbar/header chrome, since this is meant to fill a host page's own iframe
// slot (e.g. a Magento PDP tab), not look like a separate app.
function Model3DPreviewRoot({ config }: { config: ProductDesignerConfig }) {
  const { t } = useTranslation();
  useProductSetup(config);

  if (!config.glbModelUrl || config.display3dModel === false) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#94a3b8', fontSize: 14 }}>
        {t('designer.preview3d.unavailable')}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <Model3DPreview />
    </div>
  );
}
